/**
 * Records what kind of products the visitor looks at.
 *
 * `add-to-recently-viewed.js` already stores viewed *handles*, which is all the
 * recently-viewed rows need. Personalised recommendations also need to know what
 * those products *were* — type, tags, vendor — to spot a related product the
 * visitor has not seen yet. Rather than fetching each handle back to read its
 * attributes, this captures them at view time, when Liquid already has them.
 *
 * Stored under its own key so the existing recently-viewed behaviour is
 * untouched. Consumed by `personalised-recommendations.js`.
 *
 * First-party only: product attributes in the visitor's own browser. No personal
 * data, nothing transmitted.
 */

if (!customElements.get('product-affinity')) {
  const MAX_ITEMS = 24;

  /** Tags per product, capped — enough to characterise it without bloating storage. */
  const MAX_TAGS = 8;

  customElements.define(
    'product-affinity',
    class ProductAffinity extends HTMLElement {
      connectedCallback() {
        const handle = this.dataset.handle;
        const shopId = this.dataset.shopId;
        if (!handle || !shopId) return;

        const key = 'product_affinity_' + shopId;

        const entry = {
          handle,
          type: this.dataset.type || '',
          vendor: this.dataset.vendor || '',
          tags: (this.dataset.tags || '')
            .split(',')
            .map((tag) => tag.trim())
            .filter(Boolean)
            .slice(0, MAX_TAGS),
        };

        let list;
        try {
          list = JSON.parse(localStorage.getItem(key)) || [];
          if (!Array.isArray(list)) list = [];
        } catch (e) {
          list = [];
        }

        // Most recent first, deduplicated by handle — matches the ordering
        // `personalised-recommendations.js` assumes when applying recency decay.
        list = list.filter((item) => item && item.handle !== handle);
        list.unshift(entry);

        try {
          localStorage.setItem(key, JSON.stringify(list.slice(0, MAX_ITEMS)));
        } catch (e) {
          // Quota exceeded or storage blocked. Personalisation degrades to
          // Shopify's default ordering, which is acceptable.
        }
      }
    }
  );
}
